// Description-only modal for raising a Flag or OOC on an asset. Severity is chosen by
// the triggering button on AssetDetail (two separate buttons, Flag vs OOC) — this modal
// captures just the "what's wrong" context. Backend trigger updates operational_status.

import React, { useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import { Dialog } from '../components/ui/Dialog';
import { FieldRow, TextArea } from '../components/ui/Field';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';

interface Props {
  assetId: string;
  statusdesc: 'Flag' | 'OOC';
  onClose: () => void;
  onSuccess: () => void;
}

const RaiseFlagModal: React.FC<Props> = ({ assetId, statusdesc, onClose, onSuccess }) => {
  const api = useApi();
  const toast = useToast();
  const [descript, setDescript] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!descript.trim()) { toast.error('Description is required'); return; }
    setSubmitting(true);
    try {
      await api.post(`/assets/${assetId}/flags`, {
        body: { descript: descript.trim(), statusdesc },
      });
      toast.success(`${statusdesc === 'Flag' ? 'Flagged' : 'OOC set'}`);
      onSuccess();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const title = statusdesc === 'Flag' ? 'Flag' : 'Out of Commission';

  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      meta={statusdesc === 'Flag' ? 'Needs attention' : 'Out of commission, cannot deploy'}
      size="md"
      dirty={descript.trim() !== ''}
      footer={
        <>
          <button onClick={onClose} className={buttonClass('quiet', 'md')}>Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className={buttonClass('filled', 'md')}
          >
            {submitting ? 'Saving…' : statusdesc}
          </button>
        </>
      }
    >
      <FieldRow label="Description">
        <TextArea
          autoFocus
          value={descript}
          onChange={e => setDescript(e.target.value)}
          rows={4}
          placeholder="What's wrong with it?"
          className="!resize-none"
        />
      </FieldRow>
    </Dialog>
  );
};

export default RaiseFlagModal;
